import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, PoundSterling, Headphones, Gift, TreePine, Code, Globe } from "lucide-react";
import { usePartnerViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import { IntegrationsRow, DeveloperTools, DemoAccess, Benefits, HowItWorks, PartnerBenefits, TrustedProven, PartnerCTA, FAQSection } from "@/components/PartnerSharedBlocks";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const heroVisualInitial = { opacity: 0, y: 20 };
const heroVisualAnimate = { opacity: 1, y: 0 };
const heroVisualTransition = { duration: 0.5, delay: 0.3 };

export default function IntegrationHubPage() {
  usePartnerViewed("ecommerce_integrations", { itemType: "partner_service" });
  return (
    <div>
      <SEOHead title="Payment System Partner Integrations" description="Explore seamless software integrations with CanDo Payments. Connect our advanced merchant services to your favourite business tools for a smoother workflow." path="/partners/integrations" />

      {/* Hero — 2 column */}
      <section className="hero-gradient py-16 md:py-24" data-testid="integration-hub-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Partner Programme</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                Integrate payments. <span className="gradient-text">Earn recurring commission.</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
                Plug CanDo Payments into your clients&apos; stores and earn every month they process. Lower-cost, fully supported, Elavon-powered — a Stripe alternative your clients will thank you for.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/epos-partner-calculator" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-all btn-shine" data-testid="hub-cta-calculator">Calculate my earnings <ArrowRight size={16} /></Link>
                <a href="/book" className="bg-white text-brand-black hover:bg-gray-50 border border-brand-light-grey font-semibold rounded-full px-8 py-3.5 text-sm transition-colors" data-testid="hub-cta-call">Book a Call</a>
              </motion.div>
            </motion.div>
            <motion.div initial={heroVisualInitial} animate={heroVisualAnimate} transition={heroVisualTransition} className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-xl border border-brand-light-grey/60 p-6 max-w-sm mx-auto">
                <p className="text-[10px] uppercase tracking-widest text-brand-grey mb-1">Partner earnings estimate</p>
                <p className="text-3xl font-heading font-bold text-brand-aqua-dark">£2,400<span className="text-lg text-brand-grey">/yr</span></p>
                <p className="text-xs text-brand-green font-semibold mt-1">Per 10 client referrals</p>
                <div className="mt-4 space-y-2">
                  {[
                    { label: "Recurring commission", value: "Monthly" },
                    { label: "Tiered rates", value: "The more you refer, the more you earn" },
                    { label: "iPad draw", value: "Every new partner, every month" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between text-sm border-t border-brand-light-grey/60 pt-2">
                      <span className="text-brand-grey">{item.label}</span>
                      <span className="text-xs font-bold text-brand-black">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Eyebrow strip */}
      <section className="py-4 bg-brand-aqua-light border-y border-brand-aqua/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: PoundSterling, text: "Recurring commission" },
              { icon: Headphones, text: "UK-based support" },
              { icon: Gift, text: "Monthly iPad draw" },
              { icon: TreePine, text: "Tree planted per referral" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <item.icon size={16} className="text-brand-aqua flex-shrink-0" />
                <span className="text-sm font-semibold text-brand-black">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <IntegrationsRow />
      <DeveloperTools />
      <DemoAccess />
      <Benefits />
      <HowItWorks />
      <PartnerBenefits />
      <TrustedProven />
      <FAQSection />
      <PartnerCTA />
    </div>
  );
}
